import { Selector } from "react-redux";

import { getPrimaryText, hasLocalization } from "@multiweb/reactCore/i18n";
import { IOpenedFormInfo } from "@multiweb/types/multiweb";
import { IAppState } from "@multiweb/types/react";
import { logger } from "../logger";
import { LabelInfo, NumberingGroupInfo } from "./types";

/**
 * Získá z globálního stavu podstrom pro otevřené položky.
 */
const getModel = (state: IAppState) => state.menu.openedItems;

/**
 * Získá z globálního stavu podstrom pro číslování otevřených položek.
 */
const getNumberingModel = (state: IAppState) => getModel(state).numbering;

/**
 * Získá seznam id aktuálně otevřených položek formulářů a profilů v menu.
 * @param state
 */
export const getOpenedFormsIds: Selector<IAppState, readonly string[]> = (state) => getModel(state).openedFormsIds;

/**
 * Získá údaje o otevřené položce formuláře nebo profilu v menu podle zadaného identifikátoru jeho instance.
 * @param state
 * @param formInstanceId Identifikátor instance
 */
export const getOpenedFormInfo: Selector<IAppState, IOpenedFormInfo | undefined, string> = (state, formInstanceId) => getModel(state).openedFormsInfo[formInstanceId];

/**
 * Získá kód formuláře patřící k otevřené položce se zadaným id.
 * @param state
 * @param formInstanceId
 */
export const getOpenedFormFormCode: Selector<IAppState, string | undefined, string> = (state, formInstanceId) => getOpenedFormInfo(state, formInstanceId)?.formCode;

/**
 * Získá údaje o všech aktuálně otevřených položkách.
 * @param state
 */
export const getOpenedFormsInfo: Selector<IAppState, readonly IOpenedFormInfo[]> = (state) => Object.values(getModel(state).openedFormsInfo);

/**
 * Získá aktuálně otevřené položky v opačném pořadí otevření (poslední otevřená položka je první). Není použit reselect, protože otevřených položek bývá jen malé množství.
 */
export const getOrderedOpenedFormsInfo: Selector<IAppState, readonly IOpenedFormInfo[]> = (state) => getOpenedFormsIds(state)
    .map((openedFormId) => getOpenedFormInfo(state, openedFormId))
    .filter((openedFormInfo): openedFormInfo is IOpenedFormInfo => !!openedFormInfo);

/**
 * Získá hodnotu identifikátoru instance právě aktivního formuláře. Ignoruje aktivní formuláře bez otevřené položky v menu (formuláře otevřené s showInMenu=false).
 * @param state
 */
export const getOpenedFormInstanceId: Selector<IAppState, string | null> = (state) => getModel(state).openedFormInstanceId;

/**
 * Zjistí, zdali má být zadaná položka reprezentující otevřený formulář aktivní. Ignoruje aktivní formuláře bez otevřené položky v menu (formuláře otevřené s showInMenu=false).
 * @param state
 * @param formInstanceId Identifikátor instance formuláře
 */
export const isOpenedItemActive: Selector<IAppState, boolean, string> = (state, formInstanceId) => (
    getOpenedFormInstanceId(state) === formInstanceId
);

/**
 * Získá custom title pro zadanou otevřenou položku, pokud existuje.
 * @param state
 * @param formInstanceId Identifikátor instance formuláře.
 */
export const getCustomTitle: Selector<IAppState, string | undefined, string> = (state, formInstanceId) => getModel(state).customTitles[formInstanceId];

/**
 * Získá popisek pro otevřenou položku, včetně případného pořadového čísla. Pokud popisek nelze získat, vrací undefined.
 * @param state
 * @param formInstanceId Id instance otevřené položky.
 */
export const getFormInstanceLabel: Selector<IAppState, string | undefined, string> = (state, formInstanceId) => {
    const baseLabel = getFormInstanceBaseLabel(state, formInstanceId);
    if (!baseLabel) {
        return undefined;
    }
    const instanceNumberIsVisible = isInstanceNumberVisible(state, formInstanceId);
    const instanceNumber = getInstanceNumber(state, formInstanceId);
    return instanceNumberIsVisible && instanceNumber !== undefined ? `(${instanceNumber}) ${baseLabel}` : baseLabel;
};

/**
 * Zjistí, zda se má zobrazovat pořadové číslo pro zadanou otevřenou položku.
 * @param state
 * @param formInstanceId Id instance otevřené položky.
 */
export const isInstanceNumberVisible: Selector<IAppState, boolean | undefined, string> = (state, formInstanceId) => {
    const groupKey = getGroupKey(state, formInstanceId);
    if (!groupKey) {
        logger.warn("Group key was not found for instance id %s. Cannot determine visibility of instance number.", formInstanceId);
        return undefined;
    }
    const group = getGroup(state, groupKey);
    if (!group) {
        logger.warn("Group with key %s was not found. Cannot determine visibility of instance number for instance id.", groupKey, formInstanceId);
        return undefined;
    }
    return group.hadMoreMembers;
};

/**
 * Získá pořadové číslo otevřené položky. Vypíše varování pokud hodnotu nelze získat.
 * @param state
 * @param formInstanceId Id instance otevřené položky.
 */
export const getInstanceNumber: Selector<IAppState, number | undefined, string> = (state, formInstanceId) => {
    const instanceNumber = getNumberingModel(state).numbers[formInstanceId];
    if (instanceNumber === undefined) {
        logger.warn("Cannot get numbering for opened item with id %s.", formInstanceId);
    }
    return instanceNumber;
};

/**
 * Získá základní část popisku pro otevřenou položku (název bez pořadového čísla). Pokud nelze popisek získat, vrací undefined a vypisuje varování s příčinou.
 * @param state
 * @param formInstanceId Id instance otevřené položky.
 */
export const getFormInstanceBaseLabel: Selector<IAppState, string | undefined, string> = (state, formInstanceId) => {
    const labelInfo = getFormInstanceLabelInfo(state, formInstanceId);
    if (!labelInfo) {
        logger.warn("Label info was not found for instance id %s. Cannot create label for opened item.", formInstanceId);
        return undefined;
    }
    const { labelValue, isLocKey } = labelInfo;
    if (!labelValue) {
        logger.warn("Grouping info does not contain label value for instance id %s. Cannot create label for opened item.", formInstanceId);
        return undefined;
    }
    if (isLocKey) {
        const locKeyIsLoaded = hasLocalization(state, labelValue);
        if (locKeyIsLoaded) {
            return getPrimaryText(state, labelValue);
        } else {
            logger.warn("Localization for localization key %s is not loaded. Cannot create label for opened item %s.", labelValue, formInstanceId);
            return undefined;
        }
    } else {
        return labelValue;
    }
};

/**
 * Získá informace o popisku pro zadanou otevřenou položku.
 * @param state
 * @param formInstanceId Id instance otevřené položky.
 */
export const getFormInstanceLabelInfo: Selector<IAppState, LabelInfo | undefined, string> = (state, formInstanceId) => getModel(state).labelValues[formInstanceId];

/**
 * Získá klíč skupiny odpovídající zadané otevřené položce.
 * @param state
 * @param formInstanceId Id instance otevřené položky.
 */
export const getGroupKey: Selector<IAppState, string | undefined, string> = (state, formInstanceId) => getNumberingModel(state).groupMap[formInstanceId];

/**
 * Získá skupinu číslování podle klíče.
 * @param state
 * @param groupKey Klíč skupiny.
 */
export const getGroup: Selector<IAppState, NumberingGroupInfo | undefined, string> = (state, groupKey: string) => getNumberingModel(state).groups[groupKey];

/**
 * Získá počet otevřených položek patřících do zadané skupiny. Selector není cachovaný, protože slouží pouze pro účely změn v ságách.
 * @param state
 * @param groupKey Klíč skupiny, pro kterou se počítají položky.
 */
export const getNumberOfItemsInGroup: Selector<IAppState, number, string> = (state, groupKey) => Object.values(getNumberingModel(state).groupMap)
    .filter((filteredGroup) => filteredGroup === groupKey)
    .length;

/**
 * Získá nejvyšší číslo otevřené položky v rámci zadané skupiny. Selector není cachovaný, protože slouží pouze pro účely změn v ságách.
 * @param state
 * @param groupKey Klíč skupiny.
 */
export const getMaxInstanceNumberForGroup: Selector<IAppState, number, string> = (state, groupKey) => Object.entries(getNumberingModel(state).groupMap)
    .filter(([_formInstanceId, filteredGroup]) => filteredGroup === groupKey)
    .map(([formInstanceId, _filteredGroup]) => getInstanceNumber(state, formInstanceId))
    .reduce((actualMaximum: number, instanceNumber) => {
        return instanceNumber && instanceNumber > actualMaximum ? instanceNumber : actualMaximum;
    }, 0);
